import { Badge } from '@eventbrite/eds-badge';
import { Tabs } from '@eventbrite/eds-tabs';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import React, { useEffect, useRef } from 'react';
import { createBreakpoint } from 'react-use';
import { StickyHeaderContent as StickyHeaderContentRedesign } from '../components-redesign/Main';
import { ORGANIZER_ABOUT_TAB } from '../constants';
import { useMyStatsigContext, useOrganizerContext } from '../context';
import {
    DiscoveryEventsFromView,
    Organizer,
    OrganizerCollection,
    OrganizerCollections as OrganizerCollectionsInterface,
} from '../types';
import { FollowOrganizer } from './FollowOrganizer';
import './Main.scss';
import { MainSection } from './MainSection';
import { OrganizerAbout } from './OrganizerAbout';
import { OrganizerCollections } from './OrganizerCollections';
import { OrganizerEventFollowExperiment } from './OrganizerEventFollowExperiment';
import { OrganizerEvents } from './OrganizerEvents';
import {
    TabNavigation,
    TabNavigationHeader,
    TabNavigationSection,
    TabNavLink,
    TabSection,
} from './TabNavigation';
import { useStickyHeader } from './TabNavigation/hooks';

interface MainProps {
    organizer: Organizer;
    events: DiscoveryEventsFromView;
    organizerCollections: OrganizerCollectionsInterface<OrganizerCollection>;
    userId: string;
    enableOrganizerProfileRedesign: boolean;
}
interface StickyHeaderContentProps {
    userId: string;
}
const useBreakPoint = createBreakpoint({ desktop: 792, mobile: 0 });

const StickyHeaderContent: React.FunctionComponent<StickyHeaderContentProps> = (
    props,
) => {
    const organizer = useOrganizerContext() as Organizer;
    const [size, setSize] = React.useState('mini');
    const breakPoint = useBreakPoint(useBreakPoint);
    const userId = props.userId;

    React.useEffect(() => {
        const dynamicSize = breakPoint === 'mobile' ? 'mini' : 'standard';
        setSize(dynamicSize);
    }, [breakPoint]);

    return (
        <div className="sticky-header__extra-content">
            <div className="sticky-header__organizer-name eds-text-bl">
                {organizer.name}
            </div>
            <FollowOrganizer
                organizer={organizer}
                size={size}
                userId={userId}
            />
        </div>
    );
};

export const Main: React.FunctionComponent<MainProps> = (props) => {
    const { organizerFollowExperiment } = useMyStatsigContext();
    const intersectionRef = useRef(null);
    const isSticky = useStickyHeader(intersectionRef, {
        rootMargin: '-160px 0px 150% 0px',
    });

    const hasCollections =
        props.organizerCollections.collections &&
        props.organizerCollections.collections.length > 0;

    const userId = props.userId;

    const TAB_ITEMS = [
        {
            value: 0,
            content: (
                <>
                    <OrganizerEventFollowExperiment events={props.events} />
                </>
            ),
            displayName: gettext('Events'),
        },
        hasCollections
            ? {
                  value: 1,
                  content: (
                      <>
                          <OrganizerCollections
                              organizerCollections={props.organizerCollections}
                          />
                      </>
                  ),
                  displayName: gettext('Collections'),
              }
            : null,
        {
            value: 2,
            content: (
                <>
                    <OrganizerAbout type="mobile" />
                    <OrganizerAbout type="desktop" />
                </>
            ),
            displayName: gettext('About'),
        },
    ].filter(Boolean);

    function resetScrollPosition() {
        window.scrollTo(0, 0);
    }

    useEffect(() => {
        const tabs = document.querySelectorAll('.eds-tabs__item');
        tabs.forEach((tab) => {
            tab.addEventListener('click', resetScrollPosition);
        });

        return () => {
            tabs.forEach((tab) => {
                tab.removeEventListener('click', resetScrollPosition);
            });
        };
    }, []);

    useEffect(() => {
        const tab2 = document.querySelector('#tab2 > button > span');
        const handleClick = () => {
            logEvent(ORGANIZER_ABOUT_TAB);
        };
        if (tab2) {
            tab2.addEventListener('click', handleClick);
            return () => {
                tab2.removeEventListener('click', handleClick);
            };
        }
    }, []);

    return (
        <section
            data-testid="organizer-profile"
            className={
                organizerFollowExperiment
                    ? 'organizer-profile'
                    : 'main eds-g-cell eds-g-cell-12-12 eds-g-cell-lg-10-12 eds-g-offset-lg-1-12 eds-g-cell--has-overflow'
            }
        >
            {organizerFollowExperiment && (
                <div
                    className={`${
                        isSticky ? 'organizer-sticky-container' : ''
                    }`}
                >
                    <div ref={intersectionRef}>
                        <div
                            className={`${
                                isSticky ? 'sticky' : 'sticky-initial'
                            }`}
                        >
                            <div className="follow-container">
                                <h2>{props.organizer.name}</h2>
                                <FollowOrganizer
                                    organizer={props.organizer}
                                    userId={userId}
                                />
                            </div>
                        </div>
                    </div>

                    <Tabs items={TAB_ITEMS} initialSelectedTab={0} />
                </div>
            )}

            {!organizerFollowExperiment && (
                <TabNavigation breakPoints={{ desktop: 792, mobile: 0 }}>
                    <TabNavigationHeader
                        stickyNode={
                            props.enableOrganizerProfileRedesign ? (
                                <StickyHeaderContentRedesign userId={userId} />
                            ) : (
                                <StickyHeaderContent userId={userId} />
                            )
                        }
                        stickyContentClassNames="organizer-profile__sticky-header--content eds-g-cell eds-g-cell-12-12 eds-g-cell-lg-10-12 eds-g-offset-lg-1-12 eds-g-cell--has-overflow"
                    >
                        <TabNavLink
                            id="about"
                            containerClassName="eds-show-down-sw"
                            hide="desktop"
                        >
                            {gettext('About')}
                        </TabNavLink>
                        <TabNavLink id="events">{gettext('Events')}</TabNavLink>
                        {hasCollections && (
                            <TabNavLink id="collections">
                                <span style={{ paddingRight: '4px' }}>
                                    {gettext('Collections')}
                                </span>
                                <Badge
                                    text={gettext('New')}
                                    bgColor="success"
                                />
                            </TabNavLink>
                        )}
                    </TabNavigationHeader>
                    <TabNavigationSection>
                        <TabSection
                            hide="desktop"
                            containerClassName="eds-show-down-sw"
                            intersectionOptions={{
                                rootMargin: '-160px 0px 0px 0px',
                            }}
                        >
                            <OrganizerAbout type="mobile" />
                        </TabSection>
                        <TabSection
                            id="events"
                            containerClassName="organizer-profile__events-tab-section"
                        >
                            <MainSection headerNode={gettext('Events')}>
                                <OrganizerEvents events={props.events} />
                            </MainSection>
                        </TabSection>
                        {hasCollections && (
                            <TabSection
                                id="collections"
                                containerClassName="organizer-profile__events-tab-section"
                            >
                                <MainSection
                                    headerNode={gettext('Collections')}
                                >
                                    <OrganizerCollections
                                        organizerCollections={
                                            props.organizerCollections
                                        }
                                    />
                                </MainSection>
                            </TabSection>
                        )}
                    </TabNavigationSection>
                </TabNavigation>
            )}
        </section>
    );
};
